import {
  type InfiniteData,
  infiniteQueryOptions,
  queryOptions,
} from "@tanstack/react-query";

import {
  authApi,
  catalogsApi,
  customersApi,
  dealersApi,
  dealerTechniciansApi,
  johnDeereApi,
  placesApi,
  type SupportMessage,
  supportApi,
  visitDossiersApi,
  visitsApi,
} from "@/api";

/**
 * How long a client's file, visits and John Deere status stay fresh. Hovering
 * a link to the client reads them ahead (intent preload); opening it within
 * this window reuses them instead of asking again. #513 asks for 30–60 s.
 * Writes do not wait it out: each one invalidates its resource's base key.
 */
const READ_AHEAD_STALE_MS = 30_000;

/** How often the support inbox asks for the list again (ADR kit/0029 §6). */
export const SUPPORT_INBOX_POLL_MS = 15_000;

/** How often an open support thread asks for what came after its last message. */
export const SUPPORT_THREAD_POLL_MS = 5_000;

/** How many messages a support thread reads at a time, newest first (#603). */
export const SUPPORT_MESSAGES_PAGE_SIZE = 50;

/** A page of a thread's history, and the `before` cursor of the one older, if any. */
type SupportMessagesPage = { messages: SupportMessage[]; older?: number };

// The newest page's param is null rather than undefined: it survives the disk.
type SupportMessagesData = InfiniteData<SupportMessagesPage, number | null>;

function messagesPage(messages: SupportMessage[]): SupportMessagesPage {
  // A full page may have more behind it; a short one starts the history.
  return messages.length < SUPPORT_MESSAGES_PAGE_SIZE
    ? { messages }
    : { messages, older: messages[0]?.sequence };
}

/** The newest page with what came after it appended; read afresh when it holds nothing. */
async function readNewest(
  conversationId: string,
  newest: SupportMessagesPage | undefined
): Promise<SupportMessagesPage> {
  const held = (newest?.messages ?? []).filter((m) => !isSendingReply(m));
  const after = held.at(-1)?.sequence;
  if (after === undefined) {
    return messagesPage(
      await supportApi.listMessages(conversationId, {
        limit: SUPPORT_MESSAGES_PAGE_SIZE,
      })
    );
  }
  const fresh = await supportApi.listMessages(conversationId, { after });
  return {
    ...newest,
    messages: [...held, ...fresh.filter((m) => m.sequence > after)],
  };
}

/** The first wait before asking again about a queued reply (#520). */
export const REPLY_POLL_BASE_MS = 1_000;

/** A queued reply is asked about at least this often (#520). */
const REPLY_POLL_MAX_MS = 30_000;

/** Doubles the wait after each answer that is still queued: 1 → 2 → 4 s, capped. */
function replyPollDelay(answers: number): number {
  return Math.min(
    REPLY_POLL_BASE_MS * 2 ** Math.max(answers - 1, 0),
    REPLY_POLL_MAX_MS
  );
}

/**
 * A reply shown before the server has it: no sequence yet, so it sorts last.
 * Saved to disk, the infinite sequence comes back as null; both count.
 */
export function isSendingReply(message: SupportMessage): boolean {
  return !Number.isFinite(message.sequence);
}

/**
 * Layer 2: the only place query keys exist. One entrypoint, one namespace per
 * resource. Each namespace exposes `base` — the key prefix to invalidate the
 * whole resource — and a factory per query.
 *
 * Never write a queryKey array anywhere else: `invalidateQueries({ queryKey:
 * query.user.base })` is the supported way to blow away everything about a
 * user, and it only works if nothing invents keys on the side.
 */
export const query = {
  user: {
    base: ["user"],
    me: () =>
      queryOptions({
        queryKey: [...query.user.base, "me"],
        queryFn: authApi.fetchCurrentUser,
        // A 401 is an answer, not an outage: retrying just delays the redirect.
        retry: false,
        // Route guards read the cache synchronously; revalidate only when stale.
        staleTime: 5 * 60 * 1000,
      }),
  },

  johnDeere: {
    base: ["johnDeere"],
    unassignedConnections: () =>
      queryOptions({
        queryKey: [...query.johnDeere.base, "unassignedConnections"],
        queryFn: johnDeereApi.getUnassignedConnections,
      }),
    allConnections: () =>
      queryOptions({
        queryKey: [...query.johnDeere.base, "allConnections"],
        queryFn: johnDeereApi.getAllConnections,
      }),
    accountStatus: (accountId: string) =>
      queryOptions({
        queryKey: [...query.johnDeere.base, "accountStatus", accountId],
        queryFn: () => johnDeereApi.getAccountStatus(accountId),
        // Every John Deere write (sync, revoke, assign) invalidates johnDeere.base.
        staleTime: READ_AHEAD_STALE_MS,
      }),
    liveMachines: (accountId: string) =>
      queryOptions({
        queryKey: [...query.johnDeere.base, "liveMachines", accountId],
        queryFn: () => johnDeereApi.getLiveMachines(accountId),
        staleTime: 60_000,
      }),
    // The catalog barely changes: a model searched once is not asked again.
    equipmentModels: (q: string) =>
      queryOptions({
        queryKey: [...query.johnDeere.base, "equipmentModels", q],
        queryFn: () => johnDeereApi.searchEquipmentModels(q),
        staleTime: Number.POSITIVE_INFINITY,
      }),
  },

  // Technologies, crops and hybrids barely change: the client file reads them
  // once, not on every client opened. Nothing in the app writes them.
  catalogs: {
    base: ["catalogs"],
    technologies: () =>
      queryOptions({
        queryKey: [...query.catalogs.base, "technologies"],
        queryFn: catalogsApi.getTechnologies,
        staleTime: Number.POSITIVE_INFINITY,
      }),
    advancedTechnologies: () =>
      queryOptions({
        queryKey: [...query.catalogs.base, "advanced-technologies"],
        queryFn: catalogsApi.getAdvancedTechnologies,
        staleTime: Number.POSITIVE_INFINITY,
      }),
    fleetTechnologies: () =>
      queryOptions({
        queryKey: [...query.catalogs.base, "fleet-technologies"],
        queryFn: catalogsApi.getFleetTechnologies,
        staleTime: Number.POSITIVE_INFINITY,
      }),
    crops: () =>
      queryOptions({
        queryKey: [...query.catalogs.base, "crops"],
        queryFn: catalogsApi.getCrops,
        staleTime: Number.POSITIVE_INFINITY,
      }),
    // `null` is the whole catalog; a crop id is only that crop's hybrids.
    varieties: (cropId: string | null) =>
      queryOptions({
        queryKey: [...query.catalogs.base, "varieties", cropId],
        queryFn: () => catalogsApi.getVarieties(cropId),
        staleTime: Number.POSITIVE_INFINITY,
      }),
    supportMaterials: (q: string) =>
      queryOptions({
        queryKey: [...query.catalogs.base, "support-materials", q],
        queryFn: () => catalogsApi.searchSupportMaterials(q),
      }),
  },

  customers: {
    base: ["customers"],
    accounts: () =>
      queryOptions({
        queryKey: [...query.customers.base, "accounts"],
        queryFn: customersApi.getAccounts,
      }),
    account: (accountId: string) =>
      queryOptions({
        queryKey: [...query.customers.base, "account", accountId],
        queryFn: () => customersApi.getAccount(accountId),
      }),
    accountDetail: (accountId: string) =>
      queryOptions({
        queryKey: [...query.customers.base, "detail", accountId],
        queryFn: () => customersApi.getAccountDetail(accountId),
        // Every write to the account invalidates customers.base.
        staleTime: READ_AHEAD_STALE_MS,
      }),
    fields: (accountId: string) =>
      queryOptions({
        queryKey: [...query.customers.base, "fields", accountId],
        queryFn: () => customersApi.getFields(accountId),
      }),
    fieldZones: () =>
      queryOptions({
        queryKey: [...query.customers.base, "field-zones"],
        queryFn: customersApi.getFieldZones,
      }),
    contacts: (accountId: string) =>
      queryOptions({
        queryKey: [...query.customers.base, "contacts", accountId],
        queryFn: () => customersApi.getContacts(accountId),
      }),
    contact: (accountId: string, contactId: string) =>
      queryOptions({
        queryKey: [...query.customers.base, "contact", accountId, contactId],
        queryFn: () => customersApi.getContact(accountId, contactId),
      }),
    equipment: (accountId: string) =>
      queryOptions({
        queryKey: [...query.customers.base, "equipment", accountId],
        queryFn: () => customersApi.getEquipment(accountId),
      }),
    equipmentItem: (accountId: string, equipmentId: string) =>
      queryOptions({
        queryKey: [
          ...query.customers.base,
          "equipmentItem",
          accountId,
          equipmentId,
        ],
        queryFn: () => customersApi.getEquipmentItem(accountId, equipmentId),
      }),
  },
  visits: {
    base: ["visits"],
    forAccount: (accountId: string) =>
      queryOptions({
        queryKey: [...query.visits.base, "account", accountId],
        queryFn: () => visitsApi.getAccountVisits(accountId),
        // Every visit write invalidates visits.base.
        staleTime: READ_AHEAD_STALE_MS,
      }),
    visit: (visitId: string) =>
      queryOptions({
        queryKey: [...query.visits.base, "visit", visitId],
        queryFn: () => visitsApi.getVisit(visitId),
      }),
    report: (visitId: string) =>
      queryOptions({
        queryKey: [...query.visits.base, "report", visitId],
        queryFn: () => visitsApi.getReport(visitId),
      }),
    attachments: (visitId: string) =>
      queryOptions({
        queryKey: [...query.visits.base, "attachments", visitId],
        queryFn: () => visitsApi.getReportAttachments(visitId),
      }),
    participantUsers: (visitId: string) =>
      queryOptions({
        queryKey: [...query.visits.base, "participantUsers", visitId],
        queryFn: () => visitsApi.getParticipantUsers(visitId),
      }),
  },
  visitDossiers: {
    base: ["visitDossiers"],
    dossier: (visitId: string) =>
      queryOptions({
        queryKey: [...query.visitDossiers.base, visitId],
        queryFn: () => visitDossiersApi.getDossier(visitId),
      }),
  },
  dealers: {
    base: ["dealers"],
    // ponytail: the first page of the catalog; search as you type if it grows.
    all: () =>
      queryOptions({
        queryKey: [...query.dealers.base, "all"],
        queryFn: () => dealersApi.searchDealers(""),
      }),
  },
  dealerTechnicians: {
    base: ["dealerTechnicians"],
    search: (q: string) =>
      queryOptions({
        queryKey: [...query.dealerTechnicians.base, "search", q],
        queryFn: () => dealerTechniciansApi.searchDealerTechnicians(q),
      }),
  },
  places: {
    base: ["places"],
    geocode: (q: string) =>
      queryOptions({
        queryKey: [...query.places.base, "geocode", q],
        queryFn: () => placesApi.geocodePlace(q),
        staleTime: 24 * 60 * 60 * 1000,
        retry: false,
      }),
    // A town's name barely changes: a search typed once is not asked again.
    search: (q: string) =>
      queryOptions({
        queryKey: [...query.places.base, "search", q],
        queryFn: () => placesApi.searchPlaces(q),
        staleTime: 24 * 60 * 60 * 1000,
      }),
  },

  // The support console polls the BFF (ADR kit/0029 §6); no SSE yet.
  support: {
    base: ["support"],
    threads: () =>
      queryOptions({
        queryKey: [...query.support.base, "threads"],
        queryFn: supportApi.listThreads,
        refetchInterval: SUPPORT_INBOX_POLL_MS,
      }),
    thread: (conversationId: string) =>
      queryOptions({
        queryKey: [...query.support.base, "thread", conversationId],
        queryFn: () => supportApi.getThread(conversationId),
      }),
    // Pages run newest to oldest. Each poll asks only for what came after the
    // newest message held and appends it to the newest page, so a long history
    // is fetched once; older pages never change, so a poll keeps them as they are.
    messages: (conversationId: string) =>
      infiniteQueryOptions({
        queryKey: [...query.support.base, "messages", "pages", conversationId],
        initialPageParam: null as number | null,
        getNextPageParam: (oldest: SupportMessagesPage) => oldest.older,
        queryFn: async ({
          client,
          queryKey,
          pageParam,
        }): Promise<SupportMessagesPage> => {
          const cached = client.getQueryData<SupportMessagesData>(queryKey);
          if (pageParam !== null) {
            return (
              cached?.pages[cached.pageParams.indexOf(pageParam)] ??
              messagesPage(
                await supportApi.listMessages(conversationId, {
                  before: pageParam,
                  limit: SUPPORT_MESSAGES_PAGE_SIZE,
                })
              )
            );
          }
          const page = await readNewest(conversationId, cached?.pages[0]);
          // A reply on its way stays last until the history holds it; one
          // restored from disk lost its send with the page, so it goes.
          const replying = client.isMutating({
            mutationKey: query.support.replying(conversationId),
          });
          // Read again: the send may have settled while this read was out.
          const sending = replying
            ? (
                client.getQueryData<SupportMessagesData>(queryKey)?.pages[0]
                  ?.messages ?? []
              ).filter(
                (m) =>
                  isSendingReply(m) && !page.messages.some((h) => h.id === m.id)
              )
            : [];
          return { ...page, messages: [...page.messages, ...sending] };
        },
        refetchInterval: SUPPORT_THREAD_POLL_MS,
      }),
    replying: (conversationId: string) => [
      ...query.support.base,
      "replying",
      conversationId,
    ],
    reply: (conversationId: string, replyId: string) =>
      queryOptions({
        queryKey: [...query.support.base, "reply", conversationId, replyId],
        queryFn: () => supportApi.getReply(conversationId, replyId),
        refetchInterval: ({ state }) =>
          state.data && state.data.delivery_status !== "queued"
            ? false
            : replyPollDelay(state.dataUpdateCount),
      }),
  },
};
